import {Component, ViewChild} from '@angular/core';
import {KENDO_DROPDOWNS} from '@progress/kendo-angular-dropdowns';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';

import {BoiteDialogueGeneriqueComponent, BoiteDialogueMessagesComponent, ControleConteneurComponent, SimplePanelComponent} from '../../../../../../val-ammon-composants/val-composants-lib/src/public-api';
import {AeFMettreAJourRefusFacturesAchatParametres, AeFMettreAJourRefusFacturesAchatResultat, EFactTraitementService} from '../../../../../../val-ammon-services/val-services-efact-lib/src/public-api';
import {ValApiResultat} from '../../../../../../val-ammon-services/val-services-communs-lib/src/public-api';

//Clé de traduction du titre du traitement — repris dans la boîte de choix et dans la boîte de restitution
const c_Titre = "achat.refuser.titre";

//Traitement de marquage « à refuser » des factures d’achat (SU #117809)
//  Donne la main sur le marquage que pose le job de marquage (SU #117802) : annuler un marquage
//  tant que le refus n’a pas été transmis à la plateforme agréée.
//  Le refus étant irréversible côté vendeur une fois parti, la fenêtre entre le marquage et le
//  refus est le seul moment où « Annuler refus » a un sens.
//  Depuis le SU #118470, « Refuser » n’est plus proposé : le traitement ne sert plus qu’à revenir
//  en arrière sur un marquage posé à tort. Le refus manuel passe par la saisie du statut.
//  La liste déroulante et le type DonneesActionRefusFactureAchat sont conservés à dessein — remettre
//  « Refuser » tient alors en une entrée de tableau, et l’API accepte toujours refuser = true.
@Component (
{
  selector: 'val-refuser-factures-achat',
  imports:
  [
    KENDO_DROPDOWNS,
    BoiteDialogueGeneriqueComponent,
    BoiteDialogueMessagesComponent,
    SimplePanelComponent,
    ControleConteneurComponent,
    TranslocoPipe
  ],
  templateUrl: './refuser-factures-achat.component.html',
  styleUrl: './refuser-factures-achat.component.scss'
})
export class RefuserFacturesAchatComponent
{
  //Création
  //  Paramètres
  //    eFactTraitementService : Service e-Facture
  //    translocoService       : Service de traduction
  constructor (private eFactTraitementService: EFactTraitementService, private translocoService: TranslocoService)
  {
    this.titre = c_Titre;
    this.tailleLabels = 90;
    this.nombreFacturesSelectionnees = 0;
    //Les libellés sont traduits à chaque ouverture de la boîte de choix (ConstruireActions), dans la
    //langue active : ici, les traductions peuvent ne pas être encore chargées. La présélection de
    //l’action restante se fait au même moment.
    this.actions = [];
    this.actionSelectionnee = null;
  }

  //Boîte de dialogue de choix de l’action
  @ViewChild ("boiteDialogue")
  public boiteDialogue!: BoiteDialogueGeneriqueComponent<void>;

  //Boîte de dialogue de restitution
  @ViewChild ("boiteDialogueMessages")
  public boiteDialogueMessages!: BoiteDialogueMessagesComponent;

  //Clé de traduction du titre du traitement
  public titre: string;

  //Taille des labels
  public tailleLabels: number;

  //Nombre de factures de la sélection en cours — affiché pour que l’utilisateur sache sur quoi il agit
  public nombreFacturesSelectionnees: number;

  //Actions proposées
  public actions: DonneesActionRefusFactureAchat[];

  //Action sélectionnée
  public actionSelectionnee: DonneesActionRefusFactureAchat | null;

  //Exécute le traitement
  //  Paramètres
  //    identifiantsFactures : Identifiants des factures sélectionnées dans la grille
  //  Retourne
  //    Vrai si au moins une facture a changé de marquage — la grille doit alors être rafraîchie
  public async Executer (identifiantsFactures: string[]) : Promise<boolean>
  {
    let action: DonneesActionRefusFactureAchat | null;

    if (identifiantsFactures.length === 0)
      return false;

    action = await this.ChoisirAction (identifiantsFactures.length);

    if (action === null)
      return false;

    return this.Appliquer (identifiantsFactures, action);
  }

  //Ouvre la boîte de choix de l’action
  //  Paramètres
  //    nombreFactures : Nombre de factures de la sélection
  //  Retourne
  //    Action retenue, nulle si l’utilisateur a annulé
  private async ChoisirAction (nombreFactures: number) : Promise<DonneesActionRefusFactureAchat | null>
  {
    //« Annuler refus » est la seule action proposée, et elle est présélectionnée : la garde qui
    //imposait un choix explicite protégeait de « Refuser » obtenu par une simple validation, et
    //cette action n’est plus dans la liste. Annuler un marquage non encore transmis est réversible.
    this.ConstruireActions ();
    this.actionSelectionnee = this.actions[0];
    this.nombreFacturesSelectionnees = nombreFactures;

    await this.boiteDialogue.Ouvrir ();

    return this.actionSelectionnee;
  }

  //Construit les actions proposées, libellées dans la langue active
  private ConstruireActions () : void
  {
    this.actions =
    [
      {libelle: this.translocoService.translate ("achat.refuser.annuler-refus"), refuser: false}
    ];
  }

  //Applique l’action sur la sélection
  //  Paramètres
  //    identifiantsFactures : Identifiants des factures sélectionnées dans la grille
  //    action               : Action retenue
  //  Retourne
  //    Vrai si au moins une facture a changé de marquage
  private async Appliquer (identifiantsFactures: string[], action: DonneesActionRefusFactureAchat) : Promise<boolean>
  {
    let aefMettreAJourRefusFacturesAchatParametres: AeFMettreAJourRefusFacturesAchatParametres;
    let valApiResultat: ValApiResultat<AeFMettreAJourRefusFacturesAchatResultat>;

    try
    {
      aefMettreAJourRefusFacturesAchatParametres = new AeFMettreAJourRefusFacturesAchatParametres ();
      aefMettreAJourRefusFacturesAchatParametres.identifiantsFactures = identifiantsFactures;
      aefMettreAJourRefusFacturesAchatParametres.refuser = action.refuser;

      valApiResultat = await this.eFactTraitementService.MettreAJourRefusFacturesAchat (aefMettreAJourRefusFacturesAchatParametres);

      if (valApiResultat.status !== "success")
      {
        await this.Restituer ("exclamation.png",
                              [valApiResultat.message && valApiResultat.message.length > 0
                                 ? valApiResultat.message
                                 : this.translocoService.translate ("achat.commun.traitement-echoue", {titre: this.translocoService.translate (c_Titre)})]);

        return false;
      }

      await this.RestituerSucces (action, valApiResultat.resultat ?? new AeFMettreAJourRefusFacturesAchatResultat ());

      return (valApiResultat.resultat?.nombreFacturesModifiees ?? 0) > 0;
    }
    catch (erreur)
    {
      await this.RestituerErreurTechnique (erreur);

      return false;
    }
  }

  //Restitue le résultat d’un traitement réussi
  //  Paramètres
  //    action   : Action retenue
  //    resultat : Résultat du traitement
  private async RestituerSucces (action: DonneesActionRefusFactureAchat,
                                 resultat: AeFMettreAJourRefusFacturesAchatResultat) : Promise<void>
  {
    let messages: string[];
    let cleMessage: string;

    cleMessage = action.refuser ? "achat.refuser.factures-marquees" : "achat.refuser.factures-demarquees";

    messages = [this.translocoService.translate (cleMessage, {nombre: resultat.nombreFacturesModifiees})];

    //Les factures déjà dans l’état demandé ne sont pas retouchées : le dire évite de faire chercher
    //une trace de modification qui n’existe pas.
    if (resultat.nombreFacturesInchangees > 0)
      messages.push (this.translocoService.translate ("achat.commun.factures-inchangees", {nombre: resultat.nombreFacturesInchangees}));

    //Les factures écartées le sont nommément, avec leur motif : le contrôle se fait à l’instant du
    //traitement, donc une facture affichée comme annulable a pu être refusée entre-temps par le job.
    //Les taire ferait croire à l’utilisateur qu’il a rattrapé un refus déjà transmis.
    if (resultat.facturesEcartees.length > 0)
    {
      messages.push (this.translocoService.translate ("achat.refuser.factures-ecartees", {nombre: resultat.facturesEcartees.length}));

      for (const ecartee of resultat.facturesEcartees)
        messages.push (`• ${ecartee.numero} — ${ecartee.motif}`);
    }

    await this.Restituer ("information.png", messages);
  }

  //Restitue une erreur technique
  //  Paramètres
  //    erreur : Erreur interceptée
  private async RestituerErreurTechnique (erreur: unknown) : Promise<void>
  {
    let messages: string[];

    messages = [this.translocoService.translate ("achat.commun.erreur-technique-traitement", {titre: this.translocoService.translate (c_Titre)})];

    if (erreur != null && typeof (erreur as {message?: unknown}).message === "string")
      messages.push ((erreur as {message: string}).message);

    await this.Restituer ("exclamation.png", messages);
  }

  //Ouvre la boîte de restitution
  //  Paramètres
  //    icone    : Icone de la boîte
  //    messages : Messages à afficher
  private async Restituer (icone: string, messages: string[]) : Promise<void>
  {
    this.boiteDialogueMessages.titre = this.translocoService.translate (c_Titre);
    this.boiteDialogueMessages.icone = icone;
    this.boiteDialogueMessages.messages = messages;

    await this.boiteDialogueMessages.Ouvrir ();
  }

  //Valide le choix de l’action
  public Valider () : void
  {
    this.boiteDialogue.fonctionRetour ();
    this.boiteDialogue.Fermer ();
  }

  //Annule le choix de l’action
  public Annuler () : void
  {
    this.actionSelectionnee = null;

    this.boiteDialogue.fonctionRetour ();
    this.boiteDialogue.Fermer ();
  }
}

//Représente une action de marquage « à refuser » proposée à l’utilisateur
export class DonneesActionRefusFactureAchat
{
  //Libellé présenté dans la liste
  public libelle!: string;

  //Vrai si l’action marque les factures « à refuser »
  public refuser!: boolean;
}
